import React, { useState } from "react";
import { Modal, ModalBody, Form, FormGroup } from "reactstrap";
import Swal from "sweetalert2";
import moment from "moment";
import { Col, Icon, Button, TooltipComponent } from "components/Component";
import { serviceDeleteWaCostRate, servicePostWaCostRate, servicePutWaCostRate } from "services/waCosts";
import { formatCurrencyIDR } from "utils/formatTransaction";
import { PROVIDER_LABEL } from "./waCostFormat";

const emptyForm = { id: "", provider: "WABA_CLOUD", rate: "", freeQuota: 1000, effectiveFrom: "" };

const errorMessage = (resp) => {
  const msg = resp?.data?.message;
  if (Array.isArray(msg)) return msg.map((m) => m.property || m).join(", ") + " tidak valid";
  return msg || "Terjadi kesalahan";
};

// Rates apply from effectiveFrom until the provider's next row starts; editing
// one recalculates every period it covers (cost is never stored).
const WaCostRates = ({ token, rates, onChanged }) => {
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const openForm = (item) => {
    setForm(item ? { ...item } : emptyForm);
    setModal(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      provider: form.provider,
      rate: Number(form.rate),
      freeQuota: Number(form.freeQuota),
      effectiveFrom: form.effectiveFrom,
    };
    setSaving(true);
    const resp = form.id
      ? await servicePutWaCostRate(token, form.id, payload)
      : await servicePostWaCostRate(token, payload);
    setSaving(false);
    if (/^20/.test(resp.status)) {
      Swal.fire("Tarif WA", form.id ? "Berhasil diubah" : "Berhasil ditambahkan", "success");
      setModal(false);
      onChanged();
    } else {
      Swal.fire("Tarif WA", errorMessage(resp), "error");
    }
  };

  const onDelete = async (item) => {
    const confirm = await Swal.fire({
      title: "Hapus tarif?",
      text: `${PROVIDER_LABEL[item.provider]} ${formatCurrencyIDR(item.rate)} mulai ${item.effectiveFrom}. Biaya periode ini akan dihitung ulang.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Hapus",
      cancelButtonText: "Batal",
    });
    if (!confirm.isConfirmed) return;
    const resp = await serviceDeleteWaCostRate(token, item.id);
    if (/^20/.test(resp.status)) {
      Swal.fire("Tarif WA", "Berhasil dihapus", "success");
      onChanged();
    } else {
      Swal.fire("Tarif WA", errorMessage(resp), "error");
    }
  };

  return (
    <React.Fragment>
      <div className="card card-bordered">
        <div className="card-inner d-flex justify-content-between align-items-center">
          <div>
            <h6 className="title mb-1">Tarif per Pesan</h6>
            <p className="text-soft mb-0">
              Berlaku mulai tanggal efektif sampai tarif berikutnya. Perubahan langsung menghitung ulang biaya.
            </p>
          </div>
          <Button color="primary" size="sm" onClick={() => openForm(null)}>
            <Icon name="plus"></Icon>
            <span>Tambah Tarif</span>
          </Button>
        </div>
        <div className="card-inner p-0">
          <table className="table table-tranx">
            <thead>
              <tr>
                <th>Provider</th>
                <th className="text-right">Tarif</th>
                <th className="text-right">Kuota Gratis / Nomor / Bulan</th>
                <th>Berlaku Mulai</th>
                <th className="text-right">Diubah</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rates.map((item) => (
                <tr key={item.id}>
                  <td>{PROVIDER_LABEL[item.provider] || item.provider}</td>
                  <td className="text-right">{formatCurrencyIDR(item.rate)}</td>
                  <td className="text-right">{item.freeQuota.toLocaleString("id-ID")}</td>
                  <td>{moment(item.effectiveFrom).format("DD MMM YYYY")}</td>
                  <td className="text-right text-soft">{moment(item.updatedAt).format("DD MMM YYYY, HH:mm")}</td>
                  <td>
                    <ul className="nk-tb-actions">
                      <li className="nk-tb-action" onClick={() => openForm(item)}>
                        <TooltipComponent
                          tag="a"
                          containerClassName="btn btn-trigger btn-icon"
                          id={"edit" + item.id}
                          icon="edit-alt-fill"
                          direction="top"
                          text="Edit"
                        />
                      </li>
                      <li className="nk-tb-action" onClick={() => onDelete(item)}>
                        <TooltipComponent
                          tag="a"
                          containerClassName="btn btn-trigger btn-icon"
                          id={"delete" + item.id}
                          icon="trash-fill"
                          direction="top"
                          text="Hapus"
                        />
                      </li>
                    </ul>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={modal} toggle={() => setModal(false)} className="modal-dialog-centered">
        <ModalBody>
          <a
            href="#cancel"
            onClick={(ev) => {
              ev.preventDefault();
              setModal(false);
            }}
            className="close"
          >
            <Icon name="cross-sm"></Icon>
          </a>
          <div className="p-2">
            <h5 className="title">{form.id ? "Edit" : "Tambah"} Tarif WA</h5>
            <Form className="row gy-4 mt-2" onSubmit={onSubmit}>
              <Col md="6">
                <FormGroup>
                  <label className="form-label">Provider</label>
                  <select
                    className="form-control"
                    value={form.provider}
                    onChange={(e) => setForm({ ...form, provider: e.target.value })}
                  >
                    {Object.entries(PROVIDER_LABEL).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <label className="form-label">Berlaku Mulai</label>
                  <input
                    type="date"
                    className="form-control position-relative"
                    required
                    value={form.effectiveFrom}
                    onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <label className="form-label">Tarif per Pesan (Rp)</label>
                  <input
                    type="number"
                    className="form-control"
                    required
                    min={0}
                    step="0.01"
                    value={form.rate}
                    onChange={(e) => setForm({ ...form, rate: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <label className="form-label">Kuota Gratis / Nomor / Bulan</label>
                  <input
                    type="number"
                    className="form-control"
                    required
                    min={0}
                    step="1"
                    value={form.freeQuota}
                    onChange={(e) => setForm({ ...form, freeQuota: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col size="12">
                <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                  <li>
                    <Button outline color="primary" type="button" onClick={() => setModal(false)}>
                      Batal
                    </Button>
                  </li>
                  <li>
                    <Button color="primary" type="submit" disabled={saving}>
                      Simpan
                    </Button>
                  </li>
                </ul>
              </Col>
            </Form>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default WaCostRates;
